// TimeSlots.js
import { useState, useEffect } from "react";
import { Button, Grid, Typography, Snackbar, Alert, Chip, IconButton, useMediaQuery } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { fetchBookingDetails } from "../services/booking";
import { formatDateToYYYYMMDD } from "../utils/timer.utils";
import { HolidayDates } from "../constants";
import TimeSlotsLoadingState from "./TimeSlotsLoadingState";

const allSlots = [
  { time: "6:00 AM - 7:00 AM" },
  { time: "7:00 AM - 8:00 AM" },
  { time: "8:00 AM - 9:00 AM" },
  { time: "9:00 AM - 10:00 AM" },
  { time: "10:00 AM - 11:00 AM" },
  { time: "11:00 AM - 12:00 PM" },
  { time: "12:00 PM - 1:00 PM" },
  { time: "1:00 PM - 2:00 PM" },
  { time: "2:00 PM - 3:00 PM" },
  { time: "3:00 PM - 4:00 PM" },
  { time: "4:00 PM - 5:00 PM" },
  { time: "5:00 PM - 6:00 PM" },
  { time: "6:00 PM - 7:00 PM" },
  { time: "7:00 PM - 8:00 PM" },
  { time: "8:00 PM - 9:00 PM" },
  { time: "9:00 PM - 10:00 PM" },
  { time: "10:00 PM - 11:00 PM" },
  { time: "11:00 PM - 12:00 AM" },
];

const SPECIALDAY_NORMAL_PRICE = 3000;
const SPECIALDAY_PEAK_PRICE = 3500;
const SPECIALDAY_BADMINTON_PRICE = 1000;

const TimeSlots = ({ onSlotSelect, court, date, onBack }) => {
  const [selectedSlots, setSelectedSlots] = useState([]);
  const [bookedSlots, setBookedSlots] = useState([]);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
  });

  const availableSlots = court.id === 1 || court.id === 2
    ? allSlots
    : allSlots.filter(slot => !["11:00 PM - 12:00 AM"].includes(slot.time));

  const showError = (message) => {
    setSnackbar({ open: true, message });
  };

  const isSmallMobile = useMediaQuery("(max-width:400px)");
  
  useEffect(() => {
    const loadBookings = async () => {
      setIsLoading(true);
      try {
        let localDate = new Date(date);
        localDate.setHours(date.getHours() + 5);
        localDate.setMinutes(date.getMinutes() + 30);
        const bookings = await fetchBookingDetails(localDate.toISOString());

        // const bookings = await fetchBookingDetails(date.toISOString());
        setBookedSlots(bookings);
        setError(null); // Clear any previous error on successful fetch
      } catch (error) {
        setError("Unable to load slots due to network connection failure. Please try again later.");
      } finally {
        setIsLoading(false);
      }
    };

    if (date) {
      loadBookings();
    }
  }, [date]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  const formattedDate = formatDateToYYYYMMDD(date);

  // Determine if each slot is booked
  const isSlotBooked = (slot) => {
    const [slotStartTime, slotEndTime] = slot.time.split(" - ");
    const slotStart = new Date(`${formattedDate.replace(/-/g, "/")} ${slotStartTime}`);
    const slotEnd = new Date(`${formattedDate.replace(/-/g, "/")} ${slotEndTime}`);
    
    // Adjust slotEnd to the next day if the end time is 12:00 AM
    if (slotEndTime === "12:00 AM") {
      slotEnd.setDate(slotEnd.getDate() + 1);
    }

    // Check if the slot time has already passed
    const now = new Date();
    const isPast = slotStart < now;

    return isPast || bookedSlots.some((booking) => {
      const bookingStart = new Date(booking.startTime);
      const bookingEnd = new Date(bookingStart.getTime() + booking.duration * 1000);
      
      return (
        booking.cid === court.id &&
        bookingStart < slotEnd &&
        bookingEnd > slotStart
      );
    });
  };

  const handleSlotClick = (index) => {
    if (selectedSlots.includes(index)) {
      // Allow deselecting only the first or last selected slot
      if (index === selectedSlots[0]) {
        setSelectedSlots(selectedSlots.slice(1)); // Remove the first selected slot
      } else if (index === selectedSlots[selectedSlots.length - 1]) {
        setSelectedSlots(selectedSlots.slice(0, -1)); // Remove the last selected slot
      } else {
        showError("You can only remove slots from the ends");
      }
    } else {
      // Ensure only consecutive slots can be selected
      if (selectedSlots.length === 0 || index === selectedSlots[selectedSlots.length - 1] + 1) {
        setSelectedSlots([...selectedSlots, index]);
      } else {
        showError("Please select consecutive time slots.");
      }
    }
  };

  // const calculateTotalPrice = () => {
  //   let totalPrice = 0;
  //   selectedSlots.forEach((slotIndex) => {
  //     const time = availableSlots[slotIndex].time;
  //     const [startTime] = time.split(" - ");
  //     const [hourPart, period] = startTime.split(" ");
  //     const [startHour] = hourPart.split(":").map(Number);
  //     // const isPeak = period === "PM" && startHour >= 6 && startHour !== 12;
  //     const isPeak = (period === "PM" && startHour >= 6 && startHour !== 12) || 
  //              (period === "AM" && (startHour === 12 || startHour < 5));
  //     const pricePerSlot = isPeak ? court.peakPrice : court.normalPrice;
  //     totalPrice += pricePerSlot;
  //   });
  //   return totalPrice;
  // };

  const calculatePriceDetails = () => {
    let totalPrice = 0;
    const breakdown = [];

    const bookingDateStr = formatDateToYYYYMMDD(date);
    const bookingDay = new Date(date).getDay(); // 0=Sun, 1=Mon, ..., 6=Sat

    const isWeekend = bookingDay === 5 || bookingDay === 6 || bookingDay === 0; // Fri=5, Sat=6, Sun=0
    const isHoliday = HolidayDates.includes(bookingDateStr);
    const isSpecialDay = isWeekend || isHoliday;

    selectedSlots.forEach((slotIndex) => {
      const time = availableSlots[slotIndex].time;
      const [startTime] = time.split(" - ");
      const [hourPart, period] = startTime.split(" ");
      const [startHour] = hourPart.split(":").map(Number);

      const isPeak = 
        (period === "PM" && startHour >= 6 && startHour !== 12) || 
        (period === "AM" && (startHour === 12 || startHour < 5));

      let pricePerSlot;

      if (court.id === 1 || court.id === 2) {
        if (isSpecialDay) {
          pricePerSlot = isPeak ? SPECIALDAY_PEAK_PRICE : SPECIALDAY_NORMAL_PRICE;
        } else {
          pricePerSlot = isPeak ? court.peakPrice : court.normalPrice;
        }
      } else if (court.id === 3) {
        if (isSpecialDay) {
          pricePerSlot = SPECIALDAY_BADMINTON_PRICE;
        } else {
          pricePerSlot = court.normalPrice;
        }
      } else {
        pricePerSlot = court.normalPrice;
      }

      totalPrice += pricePerSlot;

      breakdown.push({
        slot: time,
        label: isPeak ? "Peak Hour Rate" : "Standard Rate",
        isPeak,
        isSpecialDay,
        price: pricePerSlot,
      });
    });

    return {
      totalPrice,
      breakdown,
      isSpecialDay,
    };
  };

  const { totalPrice, breakdown } = calculatePriceDetails();

  const getGroupedBreakdown = (breakdown) => {
    const groups = {};

    breakdown.forEach((item) => {
      const key = `${item.label}-${item.price}-${item.isSpecialDay}`;
      if (!groups[key]) {
        groups[key] = { ...item, count: 1 };
      } else {
        groups[key].count += 1;
      }
    });

    return Object.values(groups);
  };

  const groupedBreakdown = getGroupedBreakdown(breakdown);

  const handleConfirmBooking = async () => {
    const bookingDetails = {
      timeRange: `${availableSlots[selectedSlots[0]].time.split(" - ")[0]} - ${
        availableSlots[selectedSlots[selectedSlots.length - 1]].time.split(" - ")[1]
      }`,
      totalPrice,
    };
    onSlotSelect(bookingDetails);
  };

  return (
    <div className="flex flex-col items-center">
      {error ? (
        <>
          <div className="text-orange-500">{error}</div>
          <Button 
            variant="contained"
            onClick={() => window.location.reload()}
            sx={{
              my: 2,
              backgroundColor: "#ffffff",
              color: "#1f2937",
            }}
          >
            Back to Home
          </Button>
        </>
        
      ) : isLoading ? (
        <TimeSlotsLoadingState />
      ) : (
        <>
          <Snackbar
              open={snackbar.open}
              autoHideDuration={2500}
              onClose={() => setSnackbar({ ...snackbar, open: false })}
              anchorOrigin={{ vertical: "top", horizontal: "center" }}
          >
            <Alert severity="error" sx={{ width: "100%" }}>
              {snackbar.message}
            </Alert>
          </Snackbar>
          <Typography> 
            {isSmallMobile ? 
              "Select a consecutive set of slots." : 
              "Select a consecutive set of time slots"
            }
            </Typography>
          <Typography variant="h7" sx={{marginBottom: "15px"}}>
            {isSmallMobile ? 
              "To start over, Clear your selections" : 
              "To start over, Clear your selections and continue"
            }
          </Typography>
          <Typography variant="body1" sx={{ mb: 2, color: "#f59e0b" }}>
            If preferred slot is unavailable for this court, please check other courts.
          </Typography>
          <IconButton
            onClick={onBack}
            sx={{
              position: "fixed",
              top: "18px",
              left: 12,
              height: "34px",
              width: "34px",
              color: "#1f2937",
              backgroundColor: "rgba(255,255,255,0.8)",
              backdropFilter: "blur(6px)",
              boxShadow: 2,
              zIndex: 900,
              "&:hover": { backgroundColor: "#fff" },
            }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Grid container spacing={2} justifyContent="flex-start">
            {availableSlots.map((slot, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <Button
                  variant={selectedSlots.includes(index) ? "contained" : "outlined"}
                  color={selectedSlots.includes(index) ? "primary" : "primary"}
                  onClick={() => handleSlotClick(index)}
                  disabled={isSlotBooked(slot)}
                  fullWidth
                  sx={{
                    "&.Mui-disabled": {
                      backgroundColor: "#e5e7eb",
                      color: "#1f2937",
                    },
                  }}
                >
                  {isSlotBooked(slot) ? "Reserved" : slot.time}
                </Button>
              </Grid>
            ))}
          </Grid>

          {selectedSlots.length > 0 && (
            <>
              <div className="fixed bottom-0 left-0 w-full bg-white/20 border-white/40 backdrop-blur-md text-gray-800 px-6 py-2 border-t shadow-md z-[999]">
                <div style={{ maxHeight: "120px", overflowY: "auto", marginBottom: "8px" }}>
                  {groupedBreakdown.some(item => item.isSpecialDay) && (
                    <Typography
                      variant="caption"
                      sx={{
                        display: "inline-block",
                        px: 1,
                        py: 0.25,
                        my: 0.5,
                        borderRadius: 1,
                        backgroundColor: "#f59e0b",
                        color: "#1f2937",
                        letterSpacing: "1.2px",
                      }}
                    >
                      Holiday/Weekend rates may apply
                    </Typography>
                  )}
                  
                  {groupedBreakdown.map((item, index) => (
                    // <Chip
                    //   label={`${item.label} - Rs. ${item.price} × ${item.count}`}
                    //   sx={{ borderRadius: '8px' }}
                    // />
                    <Typography key={index} variant="body1" color="#fff">
                        {item.label} - Rs. {item.price} × {item.count}
                    </Typography>
                  ))}
                </div>

                <div style={{ display: "flex", justifyContent: "center", gap: "10px", alignItems:"center", marginBottom: "10px" }}>
                  {/* <Typography variant="h7">
                    Total: Rs. {totalPrice}
                  </Typography> */}
                  <Chip
                      label={`Rs. ${totalPrice}`}
                      sx={{ borderRadius: '8px',fontSize: "1rem", height: "40px", px: 1, color: "#fff" }}
                    />
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleConfirmBooking}
                  >
                    Confirm
                  </Button>
                </div>
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
};

export default TimeSlots;
